import { TWidget } from 'widgets/Widget';
import { getViewType } from 'widgets/toolbox/viewtype';
import { stickyHeaderHeightMap } from 'config/stickyHeaderHeightMap';

type TEntry = {
    intersectionRatio: number;
};

/**
 * @param Widget Base widget for extending
 * @returns StickyPanel widget
 */
function StickyPanelClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory search
     * @class StickyPanel
     * @augments Widget
     * @classdesc StickyPanel helper. Should be on element before StickyPanel to observe intersection
     * The main purpose is to define anchor for StickyPanel and toggle StickyPanel.
     *
     * @example <caption>Example of StickyPanel widget usage</caption>
     * <div data-widget="stickyPanel"></div>
     */
    class StickyPanel extends Widget {
        observer?: IntersectionObserver;

        headerHeight = '';

        prefs() {
            return {
                classesShow: 'b-panel_stuck',
                ...super.prefs()
            };
        }

        /**
         * @description Initialize widget logic
         */
        init() {
            this.eventBus().on('viewtype.change', 'initObserver');

            this.initObserver();
        }

        /**
         * @description IntersectionObserver initialization
         */
        initObserver() {
            const stickyPanel = this.ref('self').get();

            if (!stickyPanel) {
                return;
            }

            if (this.observer) {
                this.destroy();
            }

            // Please note that we use hardcoded sticky header height for different viewports, but you can get it dynamically
            const headerHeight = stickyHeaderHeightMap[getViewType()];

            this.headerHeight = headerHeight;

            this.observer = new IntersectionObserver(
                ([entry]) => this.toggleStickyState(entry),
                {
                    rootMargin: `-${headerHeight}.1px 0px 0px 0px`,
                    threshold: 1
                }
            );

            this.observer?.observe(stickyPanel);
        }

        /**
         * @description Toggle sticky filters panel state
         * @param entry Anchor for apply sticky panel
         */
        toggleStickyState(entry: TEntry) {
            const stickyPanel = this.ref('self').get();

            if (!stickyPanel) {
                return;
            }

            document.body.classList.toggle(
                this.prefs().classesShow,
                (entry.intersectionRatio < 1 && stickyPanel.getBoundingClientRect().top < (Number(this.headerHeight) + 1))
            );
        }

        /**
         * @description Destroy widget logic
         */
        destroy() {
            const stickyPanel = this.ref('self').get();

            if (this.observer && stickyPanel) {
                this.observer.unobserve(stickyPanel);
                this.observer.disconnect();
            }
        }
    }

    return StickyPanel;
}

export type TStickyPanel = ReturnType<typeof StickyPanelClassCreator>;

export type TStickyPanelInstance = InstanceType<TStickyPanel>;

export default StickyPanelClassCreator;
